import React, { useContext, useRef } from 'react';
import { LogEntry, LogLevel, LogAction } from './logger-types';
import { initialiseLogger, addEntry, addLoginEntry } from './logger-impl';
import * as uuid from 'uuid';

function getSessionId(): string {
    const urlParams = new URLSearchParams(window.location.search);
    let tmpSession = urlParams.get('sid');
    let msg = 'SessionId restored';
    if (!tmpSession) {
        tmpSession = uuid.v4();
        msg = 'SessionId generated';
    }

    addLoginEntry({
        msg: msg,
        sessionId: tmpSession,
    });

    return tmpSession;
}

export const sessionId = getSessionId();

export interface SessionInfo {
    appName: string;
    identity: string;
    sessionId: string;
}

export interface LoggerContextData {
    identity: string;
    sessionInfo: SessionInfo;
    updateIdentity: (idt: string) => void;
    printLog: (
        level: LogLevel,
        action: LogAction,
        msg: string,
        data?: any,
        requestId?: string
    ) => void;
    error: (msg: string, data?: any, requestId?: string) => void;
}

export const formatError = (err: any): any => {
    const alt: any = {};
    Object.getOwnPropertyNames(err).forEach((key) => {
        alt[key] = err[key];
    });
    return alt;
};

function createLoggerInstance(
    appName: string,
    identityRef: React.MutableRefObject<string>,
    updIdentityFunc: (idt: string) => void
): LoggerContextData {
    const printLog = (
        level: LogLevel,
        action: LogAction,
        msg: string,
        data?: any,
        requestId?: string
    ): void => {
        const entry: LogEntry = {
            neonUserId: identityRef.current,
            sessionId,
            requestId,
            dateTime: new Date().toISOString(),
            level: LogLevel[level],
            message: msg,
            action: LogAction[action],
            data: data instanceof Error ? formatError(data) : data,
        };

        addEntry(level, entry);
    };

    initialiseLogger();

    const resultItem: LoggerContextData = {
        sessionInfo: {
            appName: appName,
            identity: identityRef.current,
            sessionId,
        },
        identity: identityRef.current,
        printLog: printLog,
        error: (msg: string, data?: any, requestId?: string) =>
            printLog(LogLevel.ERROR, LogAction.None, msg, data, requestId),
        updateIdentity: updIdentityFunc,
    };

    return resultItem;
}

const emptyRef = React.createRef() as React.MutableRefObject<string>;
emptyRef.current = '';
const LoggerContext = React.createContext<LoggerContextData>(
    createLoggerInstance('', emptyRef, () => {})
);

export const getPlatformInfo: any = (version: string) => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const bowser = require('bowser');
    const browser = bowser.getParser(window.navigator.userAgent);

    const search = window
        .decodeURIComponent(window.location.search)
        .replace('?', '');
    const emailKv = search
        .split('&')
        .map((kv) => kv.split('='))
        .find((kv) => kv[0] === 'email');
    const email = emailKv && emailKv[1];

    const cookies = window.navigator.cookieEnabled;
    const browserInfo = browser.parse();

    return {
        'verified email': email,
        cookies: cookies,
        appVersion: version,
        browser: browserInfo.parsedResult.browser.name,
        browserVersion: browserInfo.parsedResult.browser.version,
        os: browserInfo.parsedResult.os.name,
        osVersion: browserInfo.parsedResult.os.version,
        osName: browserInfo.parsedResult.os.versionName,
        platform: browserInfo.parsedResult.platform.type,
    };
};

export interface LoggerProviderProps {
    children: JSX.Element | JSX.Element[];
    appName: string;
}

export const useLogger = (): LoggerContextData => useContext(LoggerContext);

export const LoggerProvider = ({
    children,
    appName,
}: LoggerProviderProps): JSX.Element => {
    function updateIdentity(idt: string): void {
        identityRef.current = idt;
    }

    const identityRef = useRef('');

    const loggerInstance = createLoggerInstance(
        appName,
        identityRef,
        updateIdentity
    );

    const printLog = loggerInstance.printLog;

    return (
        <LoggerContext.Provider
            value={{
                sessionInfo: loggerInstance.sessionInfo,
                updateIdentity,
                identity: identityRef.current,
                printLog,
                error: loggerInstance.error,
            }}
        >
            {children}
        </LoggerContext.Provider>
    );
};
